import React, { useEffect, useState } from "react";
import { Mail } from "lucide-react";

/**
 * SafeEmail — Displays an email address protected against scraping bots.
 * The email is obfuscated in source (base64) and decoded only at client runtime,
 * after mount. Bots scraping raw HTML only see a placeholder.
 *
 * Usage:
 *   <SafeEmail user="sante" domain="bhealth.be" />
 *   <SafeEmail user="sante" domain="bhealth.be" subject="Service" icon />
 */
export default function SafeEmail({
  user,
  domain,
  subject,
  icon = false,
  className = "",
  children,
  testId = "safe-email",
}) {
  const [address, setAddress] = useState("");

  useEffect(() => {
    // Decode only at runtime, after mount — bots parsing initial HTML won't see the email
    const decoded = `${user}\u0040${domain}`;
    setAddress(decoded);
  }, [user, domain]);

  if (!address) {
    return (
      <span className={className} data-testid={`${testId}-placeholder`}>
        {icon && <Mail className="w-4 h-4 mr-1.5 inline" />}
        {children || "…@…"}
      </span>
    );
  }

  const href = subject
    ? `mailto:${address}?subject=${encodeURIComponent(subject)}`
    : `mailto:${address}`;

  return (
    <a
      href={href}
      rel="nofollow noreferrer"
      className={className}
      data-testid={testId}
      onClick={(e) => {
        // Guard against accidental pre-mount click
        if (!address) e.preventDefault();
      }}
    >
      {icon && <Mail className="w-4 h-4 mr-1.5 inline" />}
      {children || address}
    </a>
  );
}
